'use client';

/**
 * Primitives that need Radix, and therefore the client.
 *
 * Kept apart from `primitives.tsx` so that the purely presentational building
 * blocks can render on the server.
 */
import * as ProgressPrimitive from '@radix-ui/react-progress';
import * as SeparatorPrimitive from '@radix-ui/react-separator';
import { cn } from '@/lib/utils';

/**
 * A progress bar that states its value in text as well as in the fill, so the
 * meaning does not depend on seeing the colour or the width.
 */
export function Progress({
  value,
  max = 100,
  tone = 'brand',
  label,
  className,
}: {
  value: number;
  max?: number;
  tone?: 'brand' | 'positive' | 'warning' | 'danger';
  label: string;
  className?: string;
}) {
  const clamped = Math.max(0, Math.min(100, (value / (max || 100)) * 100));
  const fill = {
    brand: 'bg-brand',
    positive: 'bg-positive',
    warning: 'bg-warning',
    danger: 'bg-danger',
  }[tone];

  return (
    <ProgressPrimitive.Root
      value={clamped}
      max={100}
      aria-label={label}
      className={cn('relative h-2 w-full overflow-hidden rounded-full bg-surface-muted', className)}
    >
      <ProgressPrimitive.Indicator
        className={cn('h-full rounded-full transition-[width]', fill)}
        style={{ width: `${clamped}%` }}
      />
    </ProgressPrimitive.Root>
  );
}

export function Separator({ className, ...props }: React.ComponentProps<typeof SeparatorPrimitive.Root>) {
  return <SeparatorPrimitive.Root className={cn('bg-border', 'h-px w-full', className)} {...props} />;
}
